import React from "react";
import PropTypes from "prop-types";
import {getFormInputCssClass, hasMessage} from "../../../util/FormUtil";
import {PROP_NAME_CITY} from "./CityInputComponent";
import {PROP_NAME_COUNTRY_OPTIONS} from "./CountryInputComponent";
import {trimToEmpty} from "../../../util/StringUtil";

export const PROP_NAME_STREET = "street";
// Street is derived from both of the following fields
export const PROP_NAME_STREET_1 = "street1";
export const PROP_NAME_STREET_2 = "street2";
const DELIMITER = "|";

/**
 * This component expects 2 UI street fields:
 * form property: street1 (required) and
 * form property: street2 (optional)
 * At time of persistence, these are combined as one form state (street).
 */
class StreetInputComponent extends React.Component {

    constructor(props){
        super(props);
        this.checkField = this.checkField.bind(this);
        this.checkPattern = this.checkPattern.bind(this);
        this.checkRequired = this.checkRequired.bind(this);
        this.getConcatenatedValue = this.getConcatenatedValue.bind(this);
        this.getPropertyName = this.getPropertyName.bind(this);
        this.getSiblingPropertyName = this.getSiblingPropertyName.bind(this);
        this.populateStreetFieldState = this.populateStreetFieldState.bind(this);
        this.refreshFormState = this.refreshFormState.bind(this);
        this.setDirty = this.setDirty.bind(this);
        this.setValue = this.setValue.bind(this);
        this.validate = this.validate.bind(this);

        this.inputRef = React.createRef();

        const {userDataInit: {address: {maxLengthStreet, patternStreet}}} = this.props;
        const PROP_DATA = {};
        PROP_DATA[PROP_NAME_STREET] = { label: "Address", maxLength: maxLengthStreet, pattern: patternStreet};
        this.PROP_DATA = PROP_DATA;
    }

    componentDidMount() {
        this.refreshFormState(this.props.data);
        if (this.props.autoFocus) {
            this.inputRef.current.focus(); // UNO-21074: Add focus to enforce field validation
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        this.refreshFormState(this.props.data);
    }

    // Restore the street options on form refresh
    refreshFormState(data) {
        if (this.props.index == 1) { // street1 populates both
            const street1 = data[PROP_NAME_STREET_1];
            if (street1 === undefined || street1 === null) {
                this.populateStreetFieldState();
            }
        }
    }

    /**
     * Potential displayed Street fields (street1 and street2) are derived from the one street state
     */
    populateStreetFieldState() {
        const street = this.props.data[PROP_NAME_STREET];
        if (street && street.length > 0) {
            let street1;
            let street2;

            const streetFields = street.split(DELIMITER);
            street1 = streetFields[0].trim();
            if (streetFields.length > 1) {
                street2 = streetFields[1].trim();
            }

            const initMetadata = {valid: true, dirty: false, message: null};
            this.props.setFormState([{property: PROP_NAME_STREET_1, data: street1, metadata: initMetadata},
                {property: PROP_NAME_STREET_2, data: street2, metadata: initMetadata}]);
        }
    }

    setValue(e){
        const value = e.target.value;
        if (!this.containsExcludedChar(value)) {
            this.validate(value);
        }
    }

    /**
     * Distinct validation from the UserProfileCommand PATTERN_STREET, which needs to allow the '|' delimiter
     */
    containsExcludedChar(value) {
        return value.indexOf(DELIMITER) !== -1;
    }

    validate(value){
        const {required, setFormState} = this.props;
        const currentStreetPropName = this.getPropertyName();
        const streetPropInfo = this.PROP_DATA[PROP_NAME_STREET];

        // meta - reset everything
        const newMetadata = {valid: true, dirty: true, message: null};
        this.checkField(value, streetPropInfo, newMetadata, required);

        const siblingStreetPropName = this.getSiblingPropertyName();
        const siblingMetaData = this.props.metadata[siblingStreetPropName];
        const siblingMetaValid = siblingMetaData ? siblingMetaData.valid : true;

        const combinedMetadata = {
            valid: newMetadata.valid && siblingMetaValid,   // may resolve to undefined, which is ok
            dirty: true,
            message: null   // assumes only the derived attribute message needs to be captured
        };

        // current UI field and the state to persist
        setFormState([{property: currentStreetPropName, data: value, metadata: { ...newMetadata }},
                      {property: PROP_NAME_STREET, data: this.getConcatenatedValue(), metadata: { ...combinedMetadata }}]);
    }

    checkRequired(value, propInfo, metadata, required){
        metadata.valid = !required || (value && value.trim().length > 0);
        if (! metadata.valid){
            metadata.message = propInfo.label + " " + this.props.index + " is required";
        }
        return metadata.valid;
    }

    checkPattern(value="", propInfo, metadata){
        const {index} = this.props;
        // pattern test works for ';', not sure about the other special chars
        metadata.valid = propInfo.pattern.test(value);  // Delimiter '|' is allowed here
        if (! metadata.valid){
            metadata.message = propInfo.label + " " + index + " contains invalid characters"
        }
        return metadata.valid;
    }

    checkLength(value, propInfo, metadata) {
        const siblingStreetPropName = this.getSiblingPropertyName();
        const siblingValue = this.props.data[siblingStreetPropName];

        const maxLength = propInfo.maxLength - 3;   // Allow for the delimiter and spaces
        const valueLength = value ? value.length : 0;
        const siblingLength = siblingValue ? siblingValue.length : 0;
        const combinedLength = valueLength + siblingLength;

        metadata.valid = maxLength >= combinedLength;
        if (! metadata.valid) {
            metadata.message = "Address1 and Address2 exceeds the maximum combined length of " + maxLength + " characters";
        }
        return metadata.valid;
    }

    checkField(value, propInfo, metadata, required){
        if (this.checkPattern(value, propInfo, metadata)) {
            if (this.checkRequired(value, propInfo, metadata, required)) {
                this.checkLength(value, propInfo, metadata);
            }
        }
    }

    getPropertyName(){  
        return PROP_NAME_STREET + this.props.index;
    }

    getSiblingPropertyName(){
        return (this.props.index === 1) ? PROP_NAME_STREET_2 : PROP_NAME_STREET_1;
    }

    getConcatenatedValue() {
        const street1Value = trimToEmpty(this.props.data[PROP_NAME_STREET_1]);
        const street2Value = trimToEmpty(this.props.data[PROP_NAME_STREET_2]);
        if (street2Value && street2Value.length > 0) {
            return street1Value + " " + DELIMITER + " " + street2Value;
        } else {
            return street1Value;
        }
    }

    setDirty(){
        const propertyName = this.getPropertyName();
        this.validate(this.props.data[propertyName]);
    }

    render(){
        const {data, index, readOnly, required, metadata} = this.props;   //required,
        const streetAddressPropName = this.getPropertyName();
        const streetAddress = data[streetAddressPropName] || "";
        const streetAddressPropInfo = this.PROP_DATA[PROP_NAME_STREET];
        const requiredClassName = required ? "" : " optional";
        const fieldClassNameAttr = getFormInputCssClass(metadata, streetAddressPropName);

        return (
            <div className="form-fields">
                <div className={"form-fields grid__col-md-6 grid__col-sm-12" + requiredClassName}>
                    <label forName={PROP_NAME_STREET + index}>{streetAddressPropInfo.label} {index}</label>
                    {/* TODO as read-only */}
                    <input
                        className={"text-input text-input--large responsive" + fieldClassNameAttr}
                        disabled={ readOnly ? "disabled" : null}
                        id={PROP_NAME_STREET + index}
                        maxLength={streetAddressPropInfo.maxLength}
                        name={PROP_NAME_STREET + index}
                        onChange={this.setValue}
                        onBlur={this.setDirty}
                        type="text"
                        value={streetAddress}
                        ref={this.inputRef}
                    />
                    {
                        hasMessage(metadata, streetAddressPropName) &&
                        <div className="error">{metadata[streetAddressPropName].message}</div>
                    }
                </div>
            </div>
        );
    }
}


StreetInputComponent.propTypes = {
    data: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    metadata: PropTypes.object.isRequired,
    readOnly: PropTypes.object,
    required: PropTypes.bool.isRequired,
    setFormState: PropTypes.func.isRequired,
    userDataInit: PropTypes.object.isRequired
};

export default StreetInputComponent;